import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { noopStorage } from "@/lib/storage/noop-storage";
import { queryClient } from "@/lib/query-client";

import type { AuthUser, SessionImpersonation } from "./types";

export const AUTH_PERSIST_KEY = "atech-auth-session";

export type AuthStore = {
  user: AuthUser | null;
  token: string | null;
  tenantId: string | null;
  role: string | null;
  impersonation: SessionImpersonation | null;
  setSession: (payload: { user: AuthUser; token: string | null; impersonation?: SessionImpersonation | null }) => void;
  /** Update identity from `/api/auth/session` without rotating the bearer token. */
  setUser: (user: AuthUser | null) => void;
  setTenantId: (tenantId: string | null) => void;
  /** Update the in-memory bearer token used for API retries and bootstrapped sessions. */
  setToken: (token: string | null) => void;
  clearSession: () => void;
};

function impersonationKey(value: SessionImpersonation | null | undefined): string | null {
  return value?.started_at ?? null;
}

function sessionScopeKey(
  user: AuthUser | null,
  tenantId: string | null,
  impersonation: SessionImpersonation | null | undefined,
): string {
  return [
    user?.id ?? "anon",
    tenantId ?? "no-tenant",
    user?.role ?? "no-role",
    impersonationKey(impersonation) ?? "direct",
  ].join("|");
}

function resolveTenantId(nextUser: AuthUser | null, currentTenantId: string | null): string | null {
  if (!nextUser) {
    return null;
  }
  const memberships = nextUser.tenants ?? [];
  if (currentTenantId && memberships.some((tenant) => tenant.id === currentTenantId)) {
    return currentTenantId;
  }
  // Super Admins use the null tenant scope to view all workspaces by default.
  if (nextUser.role === "super_admin") {
    return null;
  }
  if (nextUser.tenantId && memberships.some((tenant) => tenant.id === nextUser.tenantId)) {
    return nextUser.tenantId;
  }
  return memberships[0]?.id ?? nextUser.tenantId ?? null;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      tenantId: null,
      role: null,
      impersonation: null,
      setSession: ({ user, token, impersonation }) => {
        const current = get();
        const nextTenantId = resolveTenantId(user, current.tenantId);
        const previousScope = sessionScopeKey(current.user, current.tenantId, current.impersonation);
        const nextScope = sessionScopeKey(user, nextTenantId, impersonation ?? null);
        if (previousScope !== nextScope) {
          void queryClient.cancelQueries();
          queryClient.clear();
        } else {
          queryClient.removeQueries({ queryKey: ["notifications"] });
        }
        set({
          user,
          token,
          tenantId: nextTenantId,
          role: user?.role ?? null,
          impersonation: impersonation ?? null,
        });
      },
      setUser: (user) => {
        const current = get();
        const nextTenantId = resolveTenantId(user, current.tenantId);
        const previousScope = sessionScopeKey(current.user, current.tenantId, current.impersonation);
        const nextScope = sessionScopeKey(user, nextTenantId, current.impersonation);
        if (previousScope !== nextScope) {
          void queryClient.cancelQueries();
          queryClient.clear();
        }
        set({
          user,
          tenantId: nextTenantId,
          role: user?.role ?? null,
        });
      },
      setToken: (token) => set({ token }),
      clearSession: () => {
        set({
          user: null,
          token: null,
          tenantId: null,
          role: null,
          impersonation: null,
        });
        queryClient.clear();
        if (typeof window !== "undefined") {
          try {
            localStorage.removeItem(AUTH_PERSIST_KEY);
          } catch {
            /* empty */
          }
        }
      },
      setTenantId: (tenantId) => {
        set({ tenantId });
        queryClient.clear();
      },
    }),
    {
      name: AUTH_PERSIST_KEY,
      version: 3,
      storage: createJSONStorage(() =>
        typeof window !== "undefined" ? localStorage : noopStorage,
      ),
      migrate: (persistedState) => {
        const state = (persistedState ?? {}) as Partial<AuthStore>;
        return {
          user: state.user ?? null,
          token: typeof state.token === "string" ? state.token : null,
          role: typeof state.role === "string" ? state.role : (state.user?.role ?? null),
          impersonation: state.impersonation ?? null,
          tenantId: typeof state.tenantId === "string" ? state.tenantId : null,
        } satisfies Partial<AuthStore>;
      },
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        role: state.role,
        impersonation: state.impersonation,
        tenantId: state.tenantId,
      }),
    },
  ),
);
